import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Loader2, Mic, Volume2, VolumeX, Wrench, Film } from "lucide-react";
import { API } from "@/lib/api";
import FloatingSlot from "@/components/FloatingSlot";
import SpeakChip from "@/components/SpeakChip";
import { useI18n } from "@/context/I18nContext";

const SESSION_KEY = "fn_ai_session_v1";
const SPEAK_PREF_KEY = "fn_ai_speak_v1";

function newSession() {
  const s = `ai_${Math.random().toString(36).slice(2, 12)}_${Date.now()}`;
  try { localStorage.setItem(SESSION_KEY, s); } catch {}
  return s;
}
function getSession() {
  try {
    const s = localStorage.getItem(SESSION_KEY);
    if (s) return s;
  } catch {}
  return newSession();
}

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { lang } = useI18n();
  // Same hide list as VoiceSearchMic — chat routes have their own composer at the bottom
  // and this floating button collides with the send button. `/live-world` has its own
  // context-aware AINA parser sheet, so we hide the global assistant there too.
  const HIDDEN_ROUTES = ["/messages", "/messenger", "/chat", "/thread", "/live-world"];
  const routeSuppressed = HIDDEN_ROUTES.some(p => routerLocation.pathname.startsWith(p));

  // Slice R⁺⁺⁺⁺⁺⁺⁺ · localized first-turn greeting. Falls back to English
  // when the code isn't in the static map — accuracy over MT for the very
  // first thing multilingual users see.
  const AINA_GREETINGS = {
    en: "Hi! I'm AINA — your Find2Near companion. Ask me anything: find plumbers nearby, explain how bookings work, or point you to safety alerts and emergency helplines.",
    hi: "नमस्ते! मैं AINA हूँ — आपकी Find2Near साथी। कुछ भी पूछें: पास के प्लंबर खोजें, बुकिंग समझें, या सुरक्षा अलर्ट और आपातकालीन नंबरों की जानकारी लें।",
    ta: "வணக்கம்! நான் AINA — உங்கள் Find2Near துணை. எதையும் கேளுங்கள்: அருகிலுள்ள பிளம்பர் தேடுங்கள், முன்பதிவுகள் புரிந்துகொள்ளுங்கள், அல்லது பாதுகாப்பு எச்சரிக்கைகளையும் அவசர எண்களையும் பெறுங்கள்.",
    te: "నమస్తే! నేను AINA — మీ Find2Near సహచరుడు. ఏమైనా అడగండి: దగ్గరలోని ప్లంబర్‌లను కనుగొనండి, బుకింగ్‌లను అర్థం చేసుకోండి, లేదా భద్రతా హెచ్చరికలు మరియు అత్యవసర నంబర్‌లను పొందండి.",
    bn: "নমস্কার! আমি AINA — আপনার Find2Near সঙ্গী। যা খুশি জিজ্ঞাসা করুন: কাছের প্লাম্বার খুঁজুন, বুকিং কীভাবে কাজ করে বুঝুন, বা নিরাপত্তা সতর্কতা ও জরুরি নম্বর পান।",
    mr: "नमस्कार! मी AINA — तुमची Find2Near सोबती. काहीही विचारा: जवळचे प्लंबर शोधा, बुकिंग समजून घ्या, किंवा सुरक्षा अलर्ट व आपत्कालीन क्रमांक मिळवा.",
    gu: "નમસ્તે! હું AINA — તમારી Find2Near સાથી. કંઈપણ પૂછો: નજીકના પ્લમ્બર શોધો, બુકિંગ સમજો, અથવા સેફ્ટી અલર્ટ અને ઈમરજન્સી નંબર મેળવો.",
    kn: "ನಮಸ್ಕಾರ! ನಾನು AINA — ನಿಮ್ಮ Find2Near ಸಂಗಾತಿ. ಏನಾದರೂ ಕೇಳಿ: ಸಮೀಪದ ಪ್ಲಂಬರ್‌ಗಳನ್ನು ಹುಡುಕಿ, ಬುಕಿಂಗ್ ಅರ್ಥಮಾಡಿಕೊಳ್ಳಿ, ಅಥವಾ ಸೇಫ್ಟಿ ಎಚ್ಚರಿಕೆಗಳು ಮತ್ತು ತುರ್ತು ಸಂಖ್ಯೆಗಳನ್ನು ಪಡೆಯಿರಿ.",
    ml: "നമസ്കാരം! ഞാൻ AINA — നിങ്ങളുടെ Find2Near കൂട്ടാളി. എന്തും ചോദിക്കൂ: അടുത്തുള്ള പ്ലംബർമാരെ കണ്ടെത്തുക, ബുക്കിംഗ് മനസ്സിലാക്കുക, അല്ലെങ്കിൽ സേഫ്റ്റി അലർട്ടുകളും അടിയന്തര നമ്പറുകളും കിട്ടുക.",
    pa: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ AINA ਹਾਂ — ਤੁਹਾਡੀ Find2Near ਸਾਥੀ। ਕੁਝ ਵੀ ਪੁੱਛੋ: ਨੇੜਲੇ ਪਲੰਬਰ ਲੱਭੋ, ਬੁਕਿੰਗ ਸਮਝੋ, ਜਾਂ ਸੇਫਟੀ ਅਲਰਟ ਅਤੇ ਐਮਰਜੈਂਸੀ ਨੰਬਰ ਲਓ।",
    ur: "السلام علیکم! میں AINA ہوں — آپ کی Find2Near ساتھی۔ کچھ بھی پوچھیں: قریبی پلمبر تلاش کریں، بکنگ سمجھیں، یا سیفٹی الرٹ اور ایمرجنسی نمبر لیں۔",
    vi: "Xin chào! Tôi là AINA — người bạn Find2Near của bạn. Hỏi tôi bất cứ điều gì: tìm thợ sửa ống nước gần đây, giải thích cách đặt dịch vụ, hoặc chỉ dẫn đến cảnh báo an toàn và số khẩn cấp.",
    th: "สวัสดี! ฉันคือ AINA — เพื่อนคู่ใจ Find2Near ของคุณ ถามอะไรก็ได้: หาช่างประปาใกล้ ๆ อธิบายการจอง หรือชี้ไปยังการแจ้งเตือนความปลอดภัยและเบอร์ฉุกเฉิน",
    id: "Halo! Saya AINA — pendamping Find2Near Anda. Tanyakan apa saja: mencari tukang ledeng terdekat, menjelaskan cara pemesanan, atau menunjukkan peringatan keselamatan dan nomor darurat.",
    ms: "Salam! Saya AINA — teman Find2Near anda. Tanya apa sahaja: cari tukang paip berdekatan, terangkan cara tempahan, atau tunjukkan amaran keselamatan dan nombor kecemasan.",
    tl: "Kumusta! Ako si AINA — ang iyong kasama sa Find2Near. Magtanong ng kahit ano: humanap ng plumber malapit sa iyo, ipaliwanag ang booking, o ituro ang mga safety alert at emergency hotline.",
    zh: "你好！我是 AINA — 你的 Find2Near 伙伴。问我任何问题：查找附近的水管工、解释预订流程，或指引安全警报与紧急电话。",
    ja: "こんにちは！私は AINA — あなたの Find2Near パートナーです。何でも聞いてください：近くの配管工を探す、予約の仕組みを説明する、安全アラートや緊急連絡先をお伝えします。",
    ko: "안녕하세요! 저는 AINA — 당신의 Find2Near 도우미입니다. 무엇이든 물어보세요: 근처 배관공 찾기, 예약 안내, 안전 경보나 응급 전화 안내.",
    ar: "مرحبا! أنا AINA — رفيقتك في Find2Near. اسأل أي شيء: ابحث عن سباك قريب، اشرح كيفية الحجز، أو أرشدك إلى تنبيهات السلامة وأرقام الطوارئ.",
    es: "¡Hola! Soy AINA — tu compañera de Find2Near. Pregúntame lo que quieras: buscar fontaneros cerca, explicar cómo funcionan las reservas o indicarte alertas de seguridad y teléfonos de emergencia.",
    fr: "Bonjour ! Je suis AINA — votre compagnon Find2Near. Demandez-moi tout : trouver un plombier à proximité, expliquer les réservations, ou vous orienter vers les alertes sécurité et numéros d'urgence.",
    pt: "Olá! Sou a AINA — sua companheira do Find2Near. Pergunte o que quiser: encontrar encanadores por perto, explicar reservas, ou apontar alertas de segurança e números de emergência.",
    de: "Hallo! Ich bin AINA — dein Find2Near-Begleiter. Frag mich alles: Klempner in der Nähe finden, Buchungen erklären, oder Sicherheitswarnungen und Notrufnummern anzeigen.",
    ru: "Здравствуйте! Я AINA — ваш помощник в Find2Near. Спросите что угодно: найти сантехника рядом, объяснить бронирование, показать оповещения безопасности и номера экстренных служб.",
  };
  const _gLang = (lang || "en").slice(0, 2);
  const _greeting = AINA_GREETINGS[_gLang] || AINA_GREETINGS.en;
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: _greeting },
  ]);
  // If the user switches language BEFORE typing anything, refresh the
  // greeting so they don't see a stale English opening.
  useEffect(() => {
    setMsgs(prev => {
      if (prev.length !== 1 || prev[0].role !== "assistant") return prev;
      return [{ role: "assistant", content: AINA_GREETINGS[_gLang] || AINA_GREETINGS.en }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_gLang]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSession());

  // ---- Voice state --------------------------------------------------------
  // Idle · recording · processing (STT round-trip) · speaking (TTS playback)
  const [voiceState, setVoiceState] = useState("idle");
  const [voiceError, setVoiceError] = useState(null);
  const [speakEnabled, setSpeakEnabled] = useState(() => {
    try { return localStorage.getItem(SPEAK_PREF_KEY) === "1"; } catch { return false; }
  });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioElRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(SPEAK_PREF_KEY, speakEnabled ? "1" : "0"); } catch {}
  }, [speakEnabled]);

  // Free the mic + player when the component unmounts or panel closes.
  useEffect(() => {
    return () => stopMicStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (!open) stopMicStream(); }, [open]);

  function stopMicStream() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) { try { t.stop(); } catch {} }
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (overrideText, opts = {}) => {
    const text = (overrideText != null ? overrideText : input).trim();
    if (!text || streaming) return;
    if (overrideText == null) setInput("");
    // The user turn we're about to append — the `mktIntent` field is filled
    // in later (asynchronously) if the /detect-intent classifier fires.
    setMsgs(m => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    let fullReply = "";

    // Marketplace intent detection — runs in parallel with the chat stream
    // so it never blocks AINA's reply. When it returns positive, we splice
    // an `mktIntent` marker onto the user message which the render surfaces
    // as a "Build this for me →" pill inline with the reply.
    (async () => {
      // --- Lightweight video-generation intent (client-side keyword hint) ---
      // No network call — this only decides whether to SHOW the pill; the
      // actual generation route is auth-gated and rate-limited server-side.
      const vidRx = /\b(make|create|generate|turn|convert)\b[^.]{0,50}\b(video|clip|ad|advert(isement)?|reel|promo|explainer)\b/i;
      if (vidRx.test(text)) {
        setMsgs(m => {
          const copy = [...m];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].role === "user" && copy[i].content === text) {
              copy[i] = { ...copy[i], videoIntent: { text } };
              break;
            }
          }
          return copy;
        });
      }
      try {
        const resp = await fetch(`${API}/marketplace/detect-intent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ text }),
        });
        if (!resp.ok) return;                        // 401 anon, 429, etc.
        const j = await resp.json();
        if (j.is_marketplace && (j.confidence || 0) >= 0.5) {
          setMsgs(m => {
            const copy = [...m];
            // Attach to the LAST user message we appended above.
            for (let i = copy.length - 1; i >= 0; i--) {
              if (copy[i].role === "user" && copy[i].content === text) {
                copy[i] = { ...copy[i], mktIntent: {
                  text, title: j.suggested_title || "",
                  confidence: j.confidence,
                } };
                break;
              }
            }
            return copy;
          });
        }
      } catch { /* silent — intent is a nice-to-have, never a blocker */ }
    })();

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: sessionId.current,
          message: text,
          language: (lang || "en").slice(0, 2),
        }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              fullReply += j.delta;
              setMsgs(m => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            } else if (j.error) {
              setMsgs(m => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond. " + j.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMsgs(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, connection issue. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      // Auto-speak on stream-complete when EITHER the user toggled TTS on,
      // OR the question was submitted by voice (hands-free loop — the user
      // is holding the phone away from their face, they need the answer read
      // aloud even if they never tapped the speaker toggle).
      const shouldSpeak = (speakEnabled || opts.fromVoice) && fullReply && fullReply.length < 2000;
      if (shouldSpeak) {
        speakReply(fullReply, { force: !!opts.fromVoice }).catch(() => {});
      }
    }
  };

  // ---- Voice ---------------------------------------------------------------
  async function beginRecording() {
    setVoiceError(null);
    if (voiceState !== "idle" || streaming) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setVoiceError("mic-unsupported");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];
      const mimeCandidates = [
        "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4",
      ];
      const mimeType = mimeCandidates.find(m => window.MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = onRecordingStopped;
      mediaRecorderRef.current = rec;
      rec.start();
      setVoiceState("recording");
    } catch (e) {
      setVoiceError("mic-denied");
      setVoiceState("idle");
      stopMicStream();
    }
  }

  function endRecording() {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch {}
    } else {
      // Nothing was captured — just release the mic.
      stopMicStream();
      setVoiceState("idle");
    }
  }

  async function onRecordingStopped() {
    // We now hold the recorded chunks.  Release the mic immediately.
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) { try { t.stop(); } catch {} }
      streamRef.current = null;
    }
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (!chunks.length) { setVoiceState("idle"); return; }
    const type = mediaRecorderRef.current?.mimeType || "audio/webm";
    mediaRecorderRef.current = null;
    const blob = new Blob(chunks, { type });
    if (blob.size < 800) {                    // ~0.5 s of typical webm/opus
      setVoiceError("too-short");
      setVoiceState("idle");
      return;
    }
    setVoiceState("processing");
    try {
      const form = new FormData();
      form.append("audio", blob, "clip.webm");
      // Slice R⁺⁺⁺⁺ · pass the user's UI language so Whisper transcribes
      // in the expected tongue. Empty when English (Whisper defaults to
      // auto-detect anyway) so we don't force English on multilingual users.
      const shortLang = (lang || "en").slice(0, 2);
      if (shortLang && shortLang !== "en") {
        form.append("language", shortLang);
      }
      const resp = await fetch(`${API}/ai/voice/transcribe`, {
        method: "POST", body: form, credentials: "include",
      });
      if (!resp.ok) {
        setVoiceError(resp.status === 429 ? "rate-limited" : "transcribe-failed");
        setVoiceState("idle");
        return;
      }
      const data = await resp.json();
      const text = (data.text || "").trim();
      setVoiceState("idle");
      if (text) await send(text, { fromVoice: true });
    } catch (e) {
      setVoiceError("network-error");
      setVoiceState("idle");
    }
  }

  async function speakReply(text, opts = {}) {
    // `force` overrides the user-preference toggle — used by the voice-in
    // loop so the reply is always audible when the question came from voice.
    if (!speakEnabled && !opts.force) return;
    setVoiceState("speaking");
    try {
      const resp = await fetch(`${API}/ai/voice/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text, voice: "alloy", format: "mp3" }),
      });
      if (!resp.ok) throw new Error("tts failed");
      const buf = await resp.arrayBuffer();
      const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
      if (audioElRef.current) {
        try { audioElRef.current.pause(); } catch {}
      }
      const a = new Audio(url);
      audioElRef.current = a;
      a.onended = () => { setVoiceState("idle"); URL.revokeObjectURL(url); };
      a.onerror = () => { setVoiceState("idle"); URL.revokeObjectURL(url); };
      await a.play();
    } catch (e) {
      setVoiceState("idle");
    }
  }

  // Slice R⁺⁺⁺⁺⁺⁺ · localized quick-prompts. When the user's UI language
  // isn't in the static map we fall back to English — accuracy over MT
  // guesswork for high-visibility chips like these.
  const QUICK_PROMPT_I18N = {
    en: ["How do I book a plumber?", "Show me govt yojna schemes",           "How does live cab tracking work?",       "Report a safety alert"],
    hi: ["प्लंबर कैसे बुक करूँ?",       "सरकारी योजनाएँ दिखाओ",                   "लाइव कैब ट्रैकिंग कैसे काम करती है?",   "सुरक्षा अलर्ट रिपोर्ट करें"],
    ta: ["பிளம்பரை எப்படி புக் செய்வது?", "அரசு திட்டங்களைக் காட்டு",              "நேரடி டாக்ஸி டிராக்கிங் எப்படி?",     "பாதுகாப்பு எச்சரிக்கை"],
    te: ["ప్లంబర్‌ను ఎలా బుక్ చేయాలి?",   "ప్రభుత్వ యోజనలు చూపించు",             "లైవ్ క్యాబ్ ట్రాకింగ్ ఎలా పని చేస్తుంది?", "భద్రతా హెచ్చరిక ఇవ్వండి"],
    bn: ["প্লাম্বার কীভাবে বুক করব?",    "সরকারী যোজনা দেখাও",                  "লাইভ ক্যাব ট্র্যাকিং কীভাবে কাজ করে?", "সুরক্ষা সতর্কতা জানান"],
    mr: ["प्लंबर कसा बुक करू?",           "सरकारी योजना दाखवा",                     "लाइव्ह कॅब ट्रॅकिंग कशी चालते?",         "सुरक्षा अलर्ट कळवा"],
    gu: ["પ્લમ્બરને કેવી રીતે બુક કરું?",   "સરકારી યોજનાઓ બતાવો",                "લાઇવ કેબ ટ્રેકિંગ કેવી રીતે?",         "સેફ્ટી એલર્ટ મોકલો"],
    kn: ["ಪ್ಲಂಬರ್‌ ಬುಕ್ ಮಾಡುವುದು ಹೇಗೆ?",   "ಸರ್ಕಾರಿ ಯೋಜನೆಗಳು ತೋರಿಸಿ",             "ಲೈವ್ ಕ್ಯಾಬ್ ಟ್ರ್ಯಾಕಿಂಗ್ ಹೇಗೆ?",       "ಸೇಫ್ಟಿ ಎಚ್ಚರಿಕೆ ವರದಿ ಮಾಡಿ"],
    ml: ["പ്ലംബറെ എങ്ങനെ ബുക്ക് ചെയ്യാം?", "സർക്കാർ പദ്ധതികൾ കാണിക്കൂ",           "ലൈവ് ക്യാബ് ട്രാക്കിംഗ് എങ്ങനെ?",       "സേഫ്റ്റി അലേർട്ട് റിപ്പോർട്ട്"],
    pa: ["ਪਲੰਬਰ ਕਿਵੇਂ ਬੁੱਕ ਕਰਾਂ?",         "ਸਰਕਾਰੀ ਯੋਜਨਾਵਾਂ ਦਿਖਾਓ",              "ਲਾਈਵ ਕੈਬ ਟਰੈਕਿੰਗ ਕਿਵੇਂ?",           "ਸੇਫਟੀ ਅਲਰਟ ਭੇਜੋ"],
    ur: ["پلمبر کیسے بک کروں؟",         "سرکاری اسکیمیں دکھاؤ",                 "لائیو کیب ٹریکنگ کیسے کام کرتی ہے؟",   "سیفٹی الرٹ رپورٹ کریں"],
    vi: ["Đặt thợ sửa ống nước thế nào?", "Xem các chương trình chính phủ",     "Theo dõi taxi trực tiếp ra sao?",       "Báo cảnh báo an toàn"],
    th: ["จองช่างประปาอย่างไร?",        "แสดงโครงการรัฐบาล",                    "ระบบติดตามแท็กซี่แบบสดทำงานอย่างไร?", "แจ้งเตือนความปลอดภัย"],
    id: ["Cara memesan tukang ledeng?", "Tampilkan program pemerintah",         "Bagaimana pelacakan taksi langsung?",   "Laporkan peringatan keselamatan"],
    ms: ["Bagaimana menempah tukang paip?", "Tunjukkan skim kerajaan",         "Bagaimana penjejakan teksi langsung?",  "Laporkan amaran keselamatan"],
    tl: ["Paano magbook ng plumber?",   "Ipakita ang mga programa ng gobyerno", "Paano ang live cab tracking?",         "Mag-report ng safety alert"],
    zh: ["如何预约水管工?",              "显示政府项目",                          "实时打车追踪如何运作?",                 "上报安全警报"],
    ja: ["配管工を予約する方法は?",       "政府の制度を表示",                       "リアルタイム配車追跡の仕組みは?",         "安全アラートを報告"],
    ko: ["배관공을 어떻게 예약하나요?",    "정부 지원 사업 보기",                    "실시간 택시 추적은 어떻게?",            "안전 경보 신고"],
    ar: ["كيف أحجز سبّاكاً؟",             "أرني برامج الحكومة",                    "كيف يعمل تتبع سيارات الأجرة؟",         "أبلغ عن تنبيه أمان"],
    es: ["¿Cómo reservo un fontanero?",  "Programas del gobierno",              "¿Cómo funciona el seguimiento en vivo?", "Reportar alerta de seguridad"],
    fr: ["Comment réserver un plombier ?", "Programmes gouvernementaux",         "Comment fonctionne le suivi en direct ?", "Signaler une alerte sécurité"],
    pt: ["Como reservar um encanador?",  "Programas do governo",                "Como funciona o rastreamento ao vivo?",  "Reportar alerta de segurança"],
    de: ["Wie buche ich einen Klempner?", "Regierungsprogramme anzeigen",       "Wie funktioniert die Live-Verfolgung?", "Sicherheitswarnung melden"],
    ru: ["Как вызвать сантехника?",       "Государственные программы",           "Как работает отслеживание такси?",      "Сообщить о безопасности"],
  };
  const quickPrompts = QUICK_PROMPT_I18N[(lang || "en").slice(0, 2)] || QUICK_PROMPT_I18N.en;

  return (
    <>
      {/* Route-suppressed on messenger/chat pages — they already have their own composer,
          and the FAB overlaps the send button + typing area. See HIDDEN_ROUTES above. */}
      {routeSuppressed ? null : (
        <>
      {/* Floating button — occupies bottom-right FAB slot (order 0). */}
      {!open && (
        <FloatingSlot zone="bottom-right" order={0} size="fab" zIndex="fab-primary">
          <button
            onClick={() => setOpen(true)}
            className="bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white rounded-full shadow-lg hover:scale-105 transition-transform w-14 h-14 grid place-items-center group relative"
            data-testid="ask-aina-fab"
            aria-label="Ask AINA — Find2Near AI assistant"
            title="Ask AINA"
          >
            <Sparkles size={22} />
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#2D5A27] border-2 border-white animate-pulse" />
            {/* Label — hidden on mobile; visible-on-hover on desktop. */}
            <span
              className="hidden md:block absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap bg-stone-900 text-white text-xs font-semibold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              Ask AINA
            </span>
          </button>
        </FloatingSlot>
      )}

      {/* Chat panel — same slot, keyboard-aware so composer stays visible. */}
      {open && (
        <FloatingSlot
          zone="bottom-right"
          order={0}
          size="fab"
          zIndex="fab-primary"
          hideOnKeyboard={false}
          className="w-[calc(100vw-2rem-var(--f2n-safe-left)-var(--f2n-safe-right))] md:w-[380px]"
        >
        <div className="h-[520px] max-h-[calc(100vh-var(--f2n-safe-top)-var(--f2n-safe-bottom)-var(--f2n-bottom-nav-h)-32px)] bg-white border border-stone-200 rounded-3xl shadow-2xl flex flex-col overflow-hidden" data-testid="aina-panel" role="dialog" aria-label="AINA assistant">
          <div className="bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Sparkles size={18} /></div>
              <div>
                <div className="heading font-extrabold text-base tracking-wide">AINA</div>
                <div className="text-[10px] uppercase tracking-widest text-white/80">Find2Near AI · Live</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSpeakEnabled(v => !v)}
                className="text-white/80 hover:text-white p-1"
                aria-label={speakEnabled ? "Mute AINA voice replies" : "Enable AINA voice replies"}
                aria-pressed={speakEnabled}
                title={speakEnabled ? "Voice replies on" : "Voice replies off"}
                data-testid="aina-speaker-toggle"
              >
                {speakEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" aria-label="Close AINA" data-testid="aina-close-btn">
                <X size={20} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[80%] text-sm rounded-2xl px-3 py-2 whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#E05E36] text-white rounded-br-sm"
                    : "bg-white text-stone-800 border border-stone-200 rounded-bl-sm"
                }`} data-testid={`ai-msg-${i}`}>
                  {m.content || (streaming && i === msgs.length - 1 ? <Loader2 size={14} className="animate-spin" /> : null)}
                </div>
                {m.role === "assistant" && m.content && !(streaming && i === msgs.length - 1) && (
                  <div className="mt-1">
                    <SpeakChip text={m.content} testId={`ai-speak-${i}`} />
                  </div>
                )}
                {m.role === "user" && m.mktIntent && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/marketplace", {
                        state: { text: m.mktIntent.text, autoSubmit: true },
                      });
                    }}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full bg-white border border-[#E05E36]/40 text-[#c94e2a] hover:bg-[#E05E36] hover:text-white transition-colors"
                    data-testid={`ai-mkt-pill-${i}`}
                    title="Turn your request into a real project"
                  >
                    <Wrench size={12} />
                    Build this for me →
                  </button>
                )}
                {m.role === "user" && m.videoIntent && (
                  <button
                    type="button"
                    onClick={async () => {
                      // Fire directly; we already have a prompt.
                      setMsgs(mm => [...mm, { role: "assistant",
                        content: "Generating your video… (this can take up to a couple of minutes)" }]);
                      try {
                        const r = await fetch(`${API}/aina/video`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ prompt: m.videoIntent.text }),
                        });
                        if (r.status === 401) {
                          setMsgs(mm => [...mm, { role: "assistant",
                            content: "Please sign in to generate videos." }]);
                          return;
                        }
                        if (!r.ok) {
                          setMsgs(mm => [...mm, { role: "assistant",
                            content: `Sorry — video generation failed (HTTP ${r.status}). Please try again.` }]);
                          return;
                        }
                        const j = await r.json();
                        setMsgs(mm => [...mm, { role: "assistant",
                          content: "Done ✨", videoUrl: j.video_url, genId: j.gen_id }]);
                      } catch {
                        setMsgs(mm => [...mm, { role: "assistant",
                          content: "Sorry — the video service is currently unreachable." }]);
                      }
                    }}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full bg-white border border-purple-400/60 text-purple-700 hover:bg-purple-600 hover:text-white transition-colors"
                    data-testid={`ai-video-pill-${i}`}
                    title="Turn your prompt into a generated video"
                  >
                    <Film size={12} />
                    Generate video →
                  </button>
                )}
                {m.role === "assistant" && m.videoUrl && (
                  <video
                    src={m.videoUrl}
                    controls playsInline
                    className="mt-2 max-w-[80%] rounded-2xl border border-stone-200"
                    data-testid={`ai-video-out-${i}`}
                  />
                )}
              </div>
            ))}
          </div>

          {msgs.length <= 2 && !streaming && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {quickPrompts.map(p => (
                <button
                  key={p}
                  onClick={() => { setInput(p); setTimeout(send, 30); }}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700"
                  data-testid={`ai-quick-${p.slice(0,10)}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-stone-200 flex items-center gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={voiceState === "recording" ? "Listening…" : "Ask AINA anything about Find2Near…"}
              className="flex-1 text-sm bg-stone-100 rounded-full px-4 py-2 outline-none focus:bg-white focus:border focus:border-[#E05E36]"
              data-testid="aina-input"
              aria-label="Type your question for AINA"
              disabled={streaming || voiceState !== "idle"}
            />
            {/* Hold-to-talk mic */}
            <button
              type="button"
              onMouseDown={beginRecording}
              onMouseUp={endRecording}
              onMouseLeave={() => { if (voiceState === "recording") endRecording(); }}
              onTouchStart={(e) => { e.preventDefault(); beginRecording(); }}
              onTouchEnd={(e) => { e.preventDefault(); endRecording(); }}
              disabled={streaming || voiceState === "processing" || voiceState === "speaking"}
              className={
                "w-10 h-10 rounded-full grid place-items-center text-white disabled:opacity-50 " +
                (voiceState === "recording"
                  ? "bg-red-600 animate-pulse ring-4 ring-red-200"
                  : voiceState === "processing"
                  ? "bg-stone-500"
                  : "bg-stone-700 hover:bg-stone-800")
              }
              data-testid="aina-mic-btn"
              aria-label={voiceState === "recording" ? "Recording — release to send" : "Hold to talk to AINA"}
              aria-pressed={voiceState === "recording"}
              title="Hold to talk"
            >
              {voiceState === "processing"
                ? <Loader2 size={16} className="animate-spin" />
                : <Mic size={16} />}
            </button>
            <button
              type="submit"
              disabled={streaming || !input.trim() || voiceState !== "idle"}
              className="w-10 h-10 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white grid place-items-center disabled:opacity-50"
              data-testid="aina-send-btn"
              aria-label="Send message to AINA"
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
          {(voiceError || voiceState !== "idle") && (
            <div className="px-4 py-1 text-[11px] text-stone-500 border-t border-stone-100" data-testid="aina-voice-status">
              {voiceState === "recording"  && "🎙  Listening… release the mic to send."}
              {voiceState === "processing" && "…transcribing your voice"}
              {voiceState === "speaking"   && "🔊 AINA is speaking"}
              {voiceState === "idle" && voiceError === "mic-denied"     && "Mic access denied — text chat still works."}
              {voiceState === "idle" && voiceError === "mic-unsupported"&& "Voice input isn't supported in this browser — text chat still works."}
              {voiceState === "idle" && voiceError === "too-short"      && "That clip was too short — try holding the mic a bit longer."}
              {voiceState === "idle" && voiceError === "rate-limited"   && "Slow down — voice limit reached. Try again in a moment."}
              {voiceState === "idle" && voiceError === "transcribe-failed" && "Transcription failed — please try again."}
              {voiceState === "idle" && voiceError === "network-error"  && "Network hiccup — please try again."}
            </div>
          )}
        </div>
        </FloatingSlot>
      )}
        </>
      )}
    </>
  );
}
